import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FileText, CheckCircle, XCircle, RefreshCw, User, ExternalLink, Search, ShieldAlert, UploadCloud, Check, ChevronDown } from 'lucide-react';

const PanelCertificados = ({ onBack }) => {
  const [empleados, setEmpleados] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [sedeSeleccionada, setSedeSeleccionada] = useState('TODAS');
  const [sincronizandoCedula, setSincronizandoCedula] = useState(null);
  const [sincronizandoSede, setSincronizandoSede] = useState(false);
  const [error, setError] = useState('');
  const [mensajeExito, setMensajeExito] = useState('');

  const API_URL = process.env.REACT_APP_API_URL;

  // LÓGICA ABAC
  const usuarioLogueado = useMemo(() => {
    try {
      const userStr = localStorage.getItem("usuario") || localStorage.getItem("user");
      return userStr ? JSON.parse(userStr) : null;
    } catch (e) {
      return null;
    }
  }, []);

  const permisosABAC = useMemo(() => {
    return typeof usuarioLogueado?.permisos_avanzados === 'string' 
      ? JSON.parse(usuarioLogueado.permisos_avanzados || '{}') 
      : (usuarioLogueado?.permisos_avanzados || {});
  }, [usuarioLogueado]);

  const cargoUpper = String(usuarioLogueado?.cargo || "").toUpperCase();
  const esAdminGlobal = cargoUpper.includes("ADMIN") || cargoUpper.includes("SUPER") || cargoUpper.includes("GERENTE") || cargoUpper.includes("DIRECTOR") || cargoUpper === "ROOT";
  const tienePermisoGestionHumana = esAdminGlobal || permisosABAC['Control de Gestión Humana'] !== undefined;

  // EFECTO PARA OCULTAR NOTIFICACIONES FLOTANTES AUTOMÁTICAMENTE
  useEffect(() => {
    if (error || mensajeExito) {
      const timer = setTimeout(() => {
        setError('');
        setMensajeExito('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, mensajeExito]);

  // Carga silenciosa de alertas e inyección en el centro de notificaciones
  const cargarAlertasSilenciosas = useCallback(async () => {
    if (!tienePermisoGestionHumana) return; 
    try {
      const res = await fetch(`${API_URL}/api/certificados/alertas`);
      const data = await res.json();
      if (data.success && data.data.length > 0) {
        // Transformamos las alertas de certificados al formato de la campanita
        const notificacionesFormateadas = data.data.map((item, idx) => ({
          id: `cert-${idx}`,
          titulo: `Certificado por vencer: ${item.nombre}`,
          descripcion: `${item.centro_trabajo} - Vence: ${new Date(item.fecha_vencimiento).toLocaleDateString()}`,
          leida: false,
          fecha: new Date().toISOString()
        }));

        // Las guardamos para que la campanita global las pueda leer
        localStorage.setItem('notificaciones_certificados', JSON.stringify(notificacionesFormateadas));
      }
    } catch (err) {
      console.error('Error al cargar alertas silenciosas:', err);
    }
  }, [API_URL, tienePermisoGestionHumana]);

  const cargarEmpleados = useCallback(async () => {
    setCargando(true);
    try {
      const res = await fetch(`${API_URL}/api/empleados`);
      const data = await res.json();
      if (data.success) {
        const empleadosActivos = data.data.filter(emp => {
          const estado = String(emp.empl_estado || emp.estado || 'ACTIVO').toUpperCase();
          const nombre = String(emp.terc_nom || emp.nombres || '').toUpperCase();
          const esRetirado = estado.includes('INACTIVO') || estado.includes('RETIRADO') || estado.includes('LIQUIDADO');
          const esEmpresa = nombre.includes(' SAS') || nombre.includes(' S.A.S') || nombre.includes(' S A S') || nombre.includes(' LTDA') || nombre.startsWith('CI ');
          return !esRetirado && !esEmpresa && nombre.trim() !== '';
        });

        const conCertificados = empleadosActivos.map(emp => {
          const cargoLower = (emp.empl_cargo || emp.cargo || '').toLowerCase();
          const palabrasClaveAlimentos = [
            'cajero', 'alimento', 'recibo', 'frutas', 'fruver', 'verduras',
            'carnes', 'bodega', 'surtidor', 'polivalente', 'paqueteo', 
            'vinipelad', 'administrador', 'conductor', 'operador', 
            'panaderia', 'panadero', 'lacteos', 'perecederos', 'carnicero'
          ];
          const requiereAlimentos = palabrasClaveAlimentos.some(palabra => cargoLower.includes(palabra));

          return {
            ...emp,
            documento: emp.terc_nit || emp.documento,
            nombres: emp.terc_nom || emp.nombres,
            cargo: emp.empl_cargo || emp.cargo || 'Colaborador',
            centroTrabajo: emp.area_nombre || emp.centroTrabajo || 'Administrativo',
            requiereAlimentos,
            medico: { archivo: emp.medico?.archivo || null, vence: emp.medico?.vence || null },
            alimentos: { archivo: emp.alimentos?.archivo || null, vence: emp.alimentos?.vence || null }
          };
        });
        setEmpleados(conCertificados);
      }
    } catch (err) {
      setError('Error al cargar empleados.');
    } finally {
      setCargando(false);
    }
  }, [API_URL]);

  useEffect(() => {
    cargarEmpleados();
    cargarAlertasSilenciosas(); 
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = 'unset'; };
  }, [cargarEmpleados, cargarAlertasSilenciosas]);

  const limpiarTexto = (texto) => {
    if (!texto) return '';
    try { return decodeURIComponent(escape(texto)); } 
    catch (e) { return String(texto); }
  };

  const evaluarEstado = (fechaVencimiento, archivo, esRequerido = true) => {
    if (!esRequerido) return { estado: 'NO REQ', color: 'bg-white/10 text-slate-300 border-white/20 shadow-none', icono: <FileText className="w-3.5 h-3.5 text-slate-400" /> };
    if (!archivo && !fechaVencimiento) return { estado: 'FALTANTE', color: 'bg-white/25 text-white border-white/40 shadow-sm', icono: <FileText className="w-3.5 h-3.5 text-white" /> };
    if (archivo && !fechaVencimiento) return { estado: 'REVISAR', color: 'bg-amber-500/40 text-amber-100 border-amber-400/50 shadow-sm', icono: <ShieldAlert className="w-3.5 h-3.5 text-amber-300" /> };
    
    const hoy = new Date();
    const vencimiento = new Date(fechaVencimiento);
    if (vencimiento > hoy) return { estado: 'VIGENTE', color: 'bg-emerald-500/40 text-emerald-100 border-emerald-400/50 shadow-sm', icono: <CheckCircle className="w-3.5 h-3.5 text-emerald-300" /> };
    return { estado: 'VENCIDO', color: 'bg-rose-500/40 text-rose-100 border-rose-400/50 shadow-sm', icono: <XCircle className="w-3.5 h-3.5 text-rose-300" /> };
  };

  const sincronizarEmpleado = async (cedula) => {
    setSincronizandoCedula(cedula);
    setError('');
    try {
      const respuesta = await fetch(`${API_URL}/api/certificados/sincronizar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cedula })
      });
      const data = await respuesta.json();

      if (data.success) {
        setMensajeExito('Sincronización exitosa.');
        await cargarEmpleados();
        await cargarAlertasSilenciosas(); 
      } else {
        setError(data.error || 'Problema al sincronizar');
        await cargarEmpleados();
      }
    } catch (err) {
      setError('Error de red al sincronizar.');
    } finally {
      setSincronizandoCedula(null);
    }
  };

  const sincronizarSedeMasivo = async () => {
    setSincronizandoSede(true);
    setError('');
    setMensajeExito('');
    try {
      const respuesta = await fetch(`${API_URL}/api/certificados/sincronizar-sede`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sede: sedeSeleccionada })
      });
      const data = await respuesta.json();

      if (data.success) {
        setMensajeExito(data.mensaje || `Sincronización masiva iniciada.`);
      } else {
        setError(`Error: ${data.error}`);
      }
    } catch (err) {
      setError('Error en el cargue masivo.');
    } finally {
      setSincronizandoSede(false);
    }
  };

  const empleadosFiltrados = empleados.filter(emp => {
    const nombreLimpio = limpiarTexto(emp.nombres).toLowerCase();
    const coincideSede = sedeSeleccionada === 'TODAS' || emp.centroTrabajo === sedeSeleccionada;
    const coincideTexto = nombreLimpio.includes(busqueda.toLowerCase()) || String(emp.documento).includes(busqueda);
    return coincideSede && coincideTexto;
  });

  const sedesUnicas = ['TODAS', ...new Set(empleados.map(e => e.centroTrabajo).filter(Boolean))];

  return (
    <div className="w-full min-h-screen py-6 px-4 flex flex-col items-center justify-center animate-fade-in relative">
      
      {/* NOTIFICACIONES FLOTANTES (TOAST) */}
      <div className="fixed top-6 right-6 z-[100] flex flex-col gap-3">
        {error && (
          <div className="bg-rose-600/95 backdrop-blur-xl border border-rose-500 text-white px-5 py-3.5 rounded-2xl shadow-2xl flex items-center gap-3 font-bold text-sm min-w-[300px]">
            <ShieldAlert className="w-5 h-5 flex-none" /> 
            <span>{error}</span>
          </div>
        )}
        {mensajeExito && (
          <div className="bg-emerald-600/95 backdrop-blur-xl border border-emerald-500 text-white px-5 py-3.5 rounded-2xl shadow-2xl flex items-center gap-3 font-bold text-sm min-w-[300px]">
            <Check className="w-5 h-5 flex-none" /> 
            <span>{mensajeExito}</span>
          </div>
        )}
      </div>

      <div className="w-full max-w-7xl mx-auto bg-white/10 backdrop-blur-3xl border border-white/35 shadow-2xl rounded-[34px] p-6 sm:p-10 relative flex flex-col h-[90vh] overflow-hidden">
        
        <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-teal-500 via-indigo-500 to-purple-500 flex-none"></div>

        {/* CABECERA */}
        <div className="flex-none flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 mb-6 border-b border-white/20 pb-6">
          <div className="flex items-center gap-4">
            {onBack && (
              <button 
                onClick={onBack}
                className="w-12 h-12 flex-none bg-white/20 hover:bg-white/30 text-white flex items-center justify-center rounded-full transition-all shadow-md border border-white/30 cursor-pointer backdrop-blur-md"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7"></path></svg>
              </button>
            )}
            <div>
              <h2 className="text-2xl sm:text-3xl font-black text-white tracking-tight drop-shadow-md">Control Masivo de Certificados</h2>
              <p className="text-xs sm:text-sm font-bold text-slate-200 mt-1">Supervisa el estado médico y de manipulación de alimentos</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row flex-wrap items-center gap-3 w-full lg:w-auto">
            <div className="relative w-full sm:w-56">
              <Search className="w-4 h-4 text-white/70 absolute left-3 top-3" />
              <input 
                type="text"
                placeholder="Buscar nombre o cédula..."
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                className="w-full pl-9 pr-4 py-2 bg-white/20 backdrop-blur-md border border-white/40 rounded-xl text-xs font-semibold text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-inner placeholder-white/70"
              />
            </div>

            <div className="relative w-full sm:w-48">
              <select
                value={sedeSeleccionada}
                onChange={(e) => setSedeSeleccionada(e.target.value)}
                className="w-full px-4 py-2 bg-white/25 backdrop-blur-xl border border-white/40 rounded-xl text-xs font-extrabold text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm appearance-none cursor-pointer"
              >
                {sedesUnicas.map((sede, idx) => (
                  <option key={idx} value={sede} className="bg-slate-50 text-slate-800 font-bold py-2">{sede}</option>
                ))}
              </select>
              <ChevronDown className="w-4 h-4 text-white absolute right-3 top-2.5 pointer-events-none" />
            </div>

            <button
              onClick={sincronizarSedeMasivo}
              disabled={sincronizandoSede}
              className="w-full sm:w-auto px-5 py-2.5 bg-emerald-600 hover:bg-emerald-500 active:scale-95 text-white rounded-xl text-xs font-black uppercase tracking-wider transition-all flex items-center justify-center gap-2 shadow-lg shadow-emerald-600/30 cursor-pointer"
            >
              <UploadCloud className={`w-4 h-4 ${sincronizandoSede ? 'animate-bounce' : ''}`} />
              {sincronizandoSede ? 'Cargando...' : 'Masivo Sede'}
            </button>
          </div>
        </div>

        {/* LISTA DE EMPLEADOS (Limpia, sin estorbar el diseño principal) */}
        <div className="flex-1 overflow-y-auto min-h-0 pr-2 pb-2 space-y-3 custom-scrollbar">
          {cargando ? (
            <div className="flex flex-col items-center justify-center h-full text-white font-bold gap-3">
              <RefreshCw className="w-8 h-8 animate-spin text-indigo-300" />
              Cargando colaboradores...
            </div>
          ) : empleadosFiltrados.length === 0 ? (
            <div className="flex items-center justify-center h-full text-white/90 font-bold text-sm">
              No se encontraron colaboradores en esta sede o con ese filtro.
            </div>
          ) : (
            empleadosFiltrados.map((emp) => {
              const med = evaluarEstado(emp.medico.vence, emp.medico.archivo, true);
              const ali = evaluarEstado(emp.alimentos.vence, emp.alimentos.archivo, emp.requiereAlimentos);
              const estaSincronizando = sincronizandoCedula === emp.documento;

              return (
                <div key={emp.documento} className="bg-black/30 hover:bg-black/40 backdrop-blur-md border border-white/10 rounded-2xl p-4 shadow-lg transition-all flex flex-col xl:flex-row items-start xl:items-center justify-between gap-4">
                  <div className="flex items-start sm:items-center gap-3 w-full xl:w-auto">
                    <div className="w-12 h-12 rounded-2xl bg-white/10 border border-white/20 flex items-center justify-center text-white font-black text-xl flex-none shadow-sm mt-0.5 sm:mt-0">
                      <User className="w-6 h-6" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="font-black text-white text-sm leading-snug whitespace-normal break-words drop-shadow-sm">{limpiarTexto(emp.nombres)}</h3>
                      <p className="text-[11px] font-bold text-slate-200 whitespace-normal break-words mt-0.5 opacity-90">C.C. {emp.documento} • <span className="text-indigo-300 font-extrabold">{limpiarTexto(emp.cargo)}</span> ({limpiarTexto(emp.centroTrabajo)})</p>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full xl:w-auto justify-end">
                    <div className="grid grid-cols-1 sm:flex sm:flex-row items-center gap-2 w-full sm:w-auto">
                      
                      {/* MÉDICO */}
                      <div className={`px-3 py-2 rounded-xl border flex items-center justify-between sm:justify-start gap-2.5 w-full sm:w-auto backdrop-blur-sm ${med.color}`}>
                        <div className="flex items-center gap-2">
                          {med.icono}
                          <div className="text-left">
                            <p className="text-[9px] font-black uppercase tracking-wider leading-none">MÉDICO: {med.estado}</p>
                            <p className="text-[10px] font-bold opacity-90 mt-0.5">Vence: {emp.medico.vence || 'N/R'}</p>
                          </div>
                        </div>
                        {emp.medico.archivo && (
                          <a href={`${API_URL}/api/empleados/ver-pdf/${emp.documento}/MEDICO`} target="_blank" rel="noreferrer" className="p-1.5 hover:bg-white/20 rounded-lg transition-colors text-white flex-none ml-2 cursor-pointer" title="Ver PDF Médico">
                            <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        )}
                      </div>

                      {/* ALIMENTOS */}
                      <div className={`px-3 py-2 rounded-xl border flex items-center justify-between sm:justify-start gap-2.5 w-full sm:w-auto backdrop-blur-sm ${ali.color}`}>
                        <div className="flex items-center gap-2">
                          {ali.icono}
                          <div className="text-left">
                            <p className="text-[9px] font-black uppercase tracking-wider leading-none">ALIMENTOS: {ali.estado}</p>
                            <p className="text-[10px] font-bold opacity-90 mt-0.5">Vence: {emp.requiereAlimentos ? (emp.alimentos.vence || 'N/R') : 'N/A'}</p>
                          </div>
                        </div>
                        {emp.requiereAlimentos && emp.alimentos.archivo && (
                          <a href={`${API_URL}/api/empleados/ver-pdf/${emp.documento}/ALIMENTOS`} target="_blank" rel="noreferrer" className="p-1.5 hover:bg-white/20 rounded-lg transition-colors text-white flex-none ml-2 cursor-pointer" title="Ver PDF Alimentos">
                            <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        )}
                      </div>
                    </div>

                    <button onClick={() => sincronizarEmpleado(emp.documento)} disabled={estaSincronizando} title="Sincronizar con el Bot" className={`h-11 sm:w-11 px-4 sm:px-0 rounded-xl font-black transition-all flex items-center justify-center gap-2 border shadow-sm flex-none cursor-pointer ${estaSincronizando ? 'bg-white/20 text-white/50 border-white/20 cursor-not-allowed' : 'bg-indigo-600/90 hover:bg-indigo-500 text-white border-indigo-400 hover:scale-105 active:scale-95 shadow-indigo-500/30'}`}>
                      <RefreshCw className={`w-4 h-4 ${estaSincronizando ? 'animate-spin' : ''}`} />
                      <span className="inline sm:hidden text-xs uppercase tracking-wider">Sincronizar</span>
                    </button>

                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default PanelCertificados;